// components/secure/BankHolidaysCard.js — Command Center → Bank holidays.
//
// Every business-day count in the portal (escrow deposit deadline, 8.0 attorney review
// and inspection, task templates) skips these dates. The list used to be loaded two
// years at a time by a seed script; now the managing broker adds next year's federal
// holidays with one button and adds or removes single dates by hand.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

export default function BankHolidaysCard({ isBroker }) {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [add, setAdd] = useState({ holiday_date: '', name: '' });
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try { setData(await api.get('/api/automation/holidays')); } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setBusy(true); setError(''); setMsg('');
        try { const r = await fn(); if (r?.message) setMsg(r.message); await load(); }
        catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    if (!data) return error ? <p className="text-sm text-red-600">{error}</p> : null;
    const shown = data.holidays.filter((h) => h.holiday_date.startsWith(String(year)));
    const years = Array.from(new Set([...data.years, new Date().getFullYear(), new Date().getFullYear() + 1])).sort();

    return (
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-slate-800">Bank holidays</h2>
                <button className="text-sm text-indigo-600 hover:underline" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show / edit'}</button>
            </div>
            <p className="text-sm text-slate-600">Business-day deadlines (escrow deposits, attorney review, inspection, task templates) skip these dates.</p>
            {data.next_year_missing && (
                <div className="mt-2 p-2 rounded bg-amber-50 text-amber-900 text-sm">
                    {data.next_year_missing} has no holidays yet — deadlines counted into {data.next_year_missing} would land on bank holidays.
                    {isBroker && <button disabled={busy} className="ml-2 underline font-medium"
                        onClick={() => act(() => api.post(`/api/automation/holidays/federal/${data.next_year_missing}`, {}))}>
                        Add the {data.next_year_missing} federal holidays</button>}
                </div>
            )}
            {msg && <p className="text-sm text-green-700 mt-2">{msg}</p>}
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            {open && (
                <div className="mt-3 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded p-1.5">
                            {years.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                        {isBroker && <button disabled={busy} className="px-3 py-1.5 text-sm bg-slate-200 rounded"
                            onClick={() => act(() => api.post(`/api/automation/holidays/federal/${year}`, {}))}>Add the {year} federal holidays</button>}
                    </div>
                    {shown.length ? (
                        <ul className="divide-y text-sm">
                            {shown.map((h) => (
                                <li key={h.holiday_date} className="py-1.5 flex justify-between gap-2">
                                    <span>{fmt(h.holiday_date)} — {h.name}</span>
                                    {isBroker && <button disabled={busy} className="text-red-600 hover:underline text-xs"
                                        onClick={() => window.confirm(`Remove ${h.name} (${h.holiday_date})?`) && act(() => api.delete(`/api/automation/holidays/${h.holiday_date}`))}>Remove</button>}
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-sm text-slate-500">No holidays on the list for {year}.</p>}
                    {isBroker && (
                        <div className="flex items-center gap-2 flex-wrap text-sm border-t pt-3">
                            <input type="date" value={add.holiday_date} onChange={(e) => setAdd({ ...add, holiday_date: e.target.value })} className="border rounded p-1.5" />
                            <input placeholder="Name (e.g. Lincoln's Birthday — IL state holiday)" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} className="border rounded p-1.5 flex-1 min-w-[220px]" />
                            <button disabled={busy || !add.holiday_date || !add.name.trim()} className="px-3 py-1.5 bg-slate-800 text-white rounded disabled:bg-indigo-300"
                                onClick={() => act(async () => { const r = await api.post('/api/automation/holidays', add); setAdd({ holiday_date: '', name: '' }); return r; })}>Add date</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
